import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Order } from '../../../store/order/api/interface';
import {
  AvailableDeliverySlots,
  DeliverySlot,
} from '../../../client/services';
import { toastHandler } from '../../../utils/toastHandler';
import { formatDate } from '../../../utils/formatTime';

interface DeliverySlotEditDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order | null | undefined;
  onDeliverySlotUpdate: (updatedOrder: Order) => void;
}

interface DeliverySlotData {
  date: string;
  time: string;
}

const slotKey = (slot?: string | null) =>
  String(slot || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const formatDateForApi = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseApiDate = (value?: string | null): Date | null => {
  if (!value) return null;
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      const [d, m, y] = value.split('/').map(Number);
      return new Date(y, m - 1, d);
    }
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    }
  } catch {
    return null;
  }
  return null;
};

const getTodayStart = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const DeliverySlotEditDialog: React.FC<DeliverySlotEditDialogProps> = ({
  open,
  onClose,
  order,
  onDeliverySlotUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<DeliverySlotData>({
    date: '',
    time: '',
  });
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [errors, setErrors] = useState<Partial<DeliverySlotData>>({});
  const [originalTime, setOriginalTime] = useState('');

  const { orderPoints, deliveryGap } = useMemo(() => {
    const items = order?.order_items || [];
    let points = 0;
    let gap = 0;

    items.forEach((item) => {
      const qty = Number(item.qty) || 1;
      const topping: any = item.order_cake?.topping_category_item;
      if (!topping) return;
      const effort = Number(topping.effortScore);
      points += (Number.isFinite(effort) && effort > 0 ? effort : 10) * qty;
      const itemGap = Number(topping.deliveryDayGap) || 0;
      if (itemGap > gap) gap = itemGap;
    });

    (order?.order_bundle_items || []).forEach((bundle) => {
      const qty = Number(bundle.qty) || 1;
      (bundle.components || []).forEach((component) => {
        const topping: any = component.category_cake?.topping_category_item;
        if (!topping) return;
        const effort = Number(topping.effortScore);
        const componentQty = (Number(component.qty) || 1) * qty;
        points +=
          (Number.isFinite(effort) && effort > 0 ? effort : 10) * componentQty;
        const itemGap = Number(topping.deliveryDayGap) || 0;
        if (itemGap > gap) gap = itemGap;
      });
    });

    return {
      orderPoints: points > 0 ? points : 10,
      deliveryGap: gap,
    };
  }, [order]);

  const loadAvailableTimeSlots = useCallback(
    async (apiDate: string, preferredTime?: string) => {
      if (!apiDate) {
        setTimeSlots([]);
        return;
      }

      setSlotsLoading(true);
      setError(null);
      try {
        // Edit must use schedule slots for the date (settings), not same-day
        // acceptance filtering — otherwise a valid current slot can disappear.
        const response = await AvailableDeliverySlots.find({
          query: {
            date: apiDate,
            points: orderPoints,
            deliveryGap,
            acceptance: false,
          },
        });

        const requiredPoints = Number(response?.requiredPoints) || orderPoints;
        const rawSlots: any[] = Array.isArray(response?.slots)
          ? response.slots
          : [];

        const scheduleSlots: string[] = rawSlots
          .filter((item: any) => item?.fromSchedule !== false)
          .map((item: any) => String(item?.slot || '').trim())
          .filter((slot: string) => Boolean(slot));

        const availableSlots: string[] = rawSlots
          .filter((item: any) => {
            // Skip orphan booking-only rows (not in settings)
            if (item?.fromSchedule === false) return false;
            if (typeof item?.isAvailable === 'boolean') {
              return item.isAvailable;
            }
            const remaining = Number(item?.remainingPoints) || 0;
            return requiredPoints > 0
              ? remaining >= requiredPoints
              : remaining > 0;
          })
          .map((item: any) => String(item?.slot || '').trim())
          .filter((slot: string) => Boolean(slot));

        // Keep current order slot selectable if it is in settings
        // (even when full), so opening edit doesn't force a wrong change.
        const preferred = preferredTime?.trim() || '';
        const preferredConfigured = scheduleSlots.find(
          (slot: string) => slotKey(slot) === slotKey(preferred)
        );

        const merged: string[] = [...availableSlots];
        if (
          preferredConfigured &&
          !merged.some(
            (slot: string) => slotKey(slot) === slotKey(preferredConfigured)
          )
        ) {
          merged.unshift(preferredConfigured);
        }

        setTimeSlots(merged);

        setFormData((prev) => {
          if (
            preferred &&
            merged.some((slot: string) => slotKey(slot) === slotKey(preferred))
          ) {
            const match = merged.find(
              (slot: string) => slotKey(slot) === slotKey(preferred)
            );
            return { ...prev, time: match || preferred };
          }
          if (
            prev.time &&
            merged.some((slot: string) => slotKey(slot) === slotKey(prev.time))
          ) {
            const match = merged.find(
              (slot: string) => slotKey(slot) === slotKey(prev.time)
            );
            return { ...prev, time: match || prev.time };
          }
          return { ...prev, time: '' };
        });
      } catch (err: any) {
        console.error('Error loading available delivery slots:', err);
        setTimeSlots([]);
        setFormData((prev) => ({ ...prev, time: '' }));
        setError(err?.message || 'Failed to load delivery slots for this date');
      } finally {
        setSlotsLoading(false);
      }
    },
    [orderPoints, deliveryGap]
  );

  useEffect(() => {
    if (!open || !order?.deliveryDate) {
      return;
    }

    const parsed = parseApiDate(order.deliveryDate.date);
    const apiDate = parsed ? formatDateForApi(parsed) : '';
    const currentTime = order.deliveryDate.time || '';

    setFormData({
      date: apiDate,
      time: currentTime,
    });
    setOriginalTime(currentTime);
    setErrors({});
    setError(null);

    if (apiDate) {
      loadAvailableTimeSlots(apiDate, currentTime);
    } else {
      setTimeSlots([]);
    }
  }, [open, order, loadAvailableTimeSlots]);

  const selectedDate = useMemo(() => {
    if (!formData.date) return null;
    return parseApiDate(formData.date);
  }, [formData.date]);

  const handleDateChange = (value: Date | null) => {
    if (!value || Number.isNaN(value.getTime())) {
      setFormData((prev) => ({ ...prev, date: '', time: '' }));
      setTimeSlots([]);
      return;
    }

    const apiDate = formatDateForApi(value);
    setFormData((prev) => ({
      ...prev,
      date: apiDate,
      time: '',
    }));
    setErrors((prev) => ({ ...prev, date: undefined, time: undefined }));
    loadAvailableTimeSlots(apiDate, originalTime);
  };

  const handleTimeChange = (event: any) => {
    const value = event.target.value as string;
    setFormData((prev) => ({ ...prev, time: value }));
    if (errors.time) {
      setErrors((prev) => ({ ...prev, time: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<DeliverySlotData> = {};

    if (!formData.date.trim()) {
      newErrors.date = 'Delivery date is required';
    }

    if (!formData.time.trim()) {
      newErrors.time = 'Delivery time slot is required';
    } else if (
      !timeSlots.some((slot) => slotKey(slot) === slotKey(formData.time))
    ) {
      newErrors.time = 'Select a delivery slot from the current schedule';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !order) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let utcDate = formData.date;
      try {
        if (/^\d{4}-\d{2}-\d{2}$/.test(formData.date)) {
          const date = new Date(`${formData.date}T00:00:00.000Z`);
          utcDate = date.toISOString();
        }
      } catch {
        utcDate = formData.date;
      }

      const deliverySlotData = {
        deliverySlot: {
          date: utcDate,
          time: formData.time.trim(),
          type: order.deliveryDate?.type || 'date',
        },
      };

      const response = await DeliverySlot.patch(order.id, deliverySlotData);

      if (response && response.success) {
        const updatedOrder = {
          ...order,
          deliveryDate: {
            ...order.deliveryDate,
            date: response.deliverySlot.date,
            time: response.deliverySlot.time,
            type: response.deliverySlot.type,
          },
        };

        onDeliverySlotUpdate(updatedOrder);
        onClose();
        toastHandler({
          message: response.message || 'Delivery slot updated successfully',
          type: 'success',
        });
      }
    } catch (err: any) {
      console.error('Error updating delivery slot:', err);
      setError(err.message || 'Failed to update delivery slot');
      toastHandler(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onClose();
  };

  const datePickerPaperSx = {
    '& .MuiPickersDay-root': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      padding: 0,
    },
    '& .MuiPickersDay-today:not(.Mui-selected)': {
      border: '1px solid',
      borderColor: 'primary.main',
    },
    '& .MuiPickersDay-root.Mui-selected': {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      '&:hover': {
        bgcolor: 'primary.dark',
      },
    },
    '& .MuiPickersDay-root.Mui-selected:focus': {
      bgcolor: 'primary.main',
    },
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Delivery Slot</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Delivery Date"
                  value={selectedDate}
                  minDate={getTodayStart()}
                  inputFormat="dd/MM/yyyy"
                  disabled={loading}
                  onChange={handleDateChange}
                  PaperProps={{ sx: datePickerPaperSx }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!errors.date}
                      helperText={errors.date}
                      disabled={loading}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                error={!!errors.time}
                disabled={loading || slotsLoading || !formData.date}
              >
                <InputLabel>Delivery Time Slot</InputLabel>
                <Select
                  value={
                    timeSlots.some(
                      (slot: string) => slotKey(slot) === slotKey(formData.time)
                    )
                      ? timeSlots.find(
                          (slot: string) =>
                            slotKey(slot) === slotKey(formData.time)
                        ) || ''
                      : ''
                  }
                  onChange={handleTimeChange}
                  label="Delivery Time Slot"
                  endAdornment={
                    slotsLoading ? (
                      <CircularProgress
                        size={18}
                        sx={{ mr: 3 }}
                        color="inherit"
                      />
                    ) : null
                  }
                >
                  {timeSlots.length === 0 && !slotsLoading ? (
                    <MenuItem value="" disabled>
                      No available slots for this date
                    </MenuItem>
                  ) : (
                    timeSlots.map((slot) => (
                      <MenuItem key={slot} value={slot}>
                        {slot}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {errors.time ? (
                  <FormHelperText>{errors.time}</FormHelperText>
                ) : originalTime &&
                  !timeSlots.some(
                    (slot: string) => slotKey(slot) === slotKey(originalTime)
                  ) ? (
                  <FormHelperText>
                    Current saved slot &quot;{originalTime}&quot; is not in the
                    schedule for this date. Pick a valid slot.
                  </FormHelperText>
                ) : null}
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Preview:
            </Typography>
            <Typography variant="body2">
              <strong>Delivery Date:</strong>{' '}
              {formData.date ? formatDate(formData.date) : 'Not selected'}
            </Typography>
            <Typography variant="body2">
              <strong>Delivery Time:</strong>{' '}
              {formData.time || 'Not selected'}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || slotsLoading || !formData.time}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeliverySlotEditDialog;
